export type MappingMode = 'create' | 'edit';

export type MappingValidationError = 'party' | 'product' | 'terms';

export type SaveDecision =
  | { kind: 'invalid'; error: MappingValidationError }
  | { kind: 'confirm-move' }
  | {
      kind: 'create';
      partyId: string;
      productVariantId: string;
      terms: string[];
    }
  | { kind: 'update'; terms: string[]; productVariantId?: string };

export function buildInitialTerms(terms?: string[] | null): string[] {
  if (!terms || terms.length === 0) return [''];
  return [...terms];
}

export function normalizeTerms(terms: readonly string[]): string[] {
  const seen = new Set<string>();
  const unique: string[] = [];
  for (const term of terms) {
    const trimmed = term.trim();
    if (trimmed.length === 0 || seen.has(trimmed)) continue;
    seen.add(trimmed);
    unique.push(trimmed);
  }
  return unique;
}

export function replaceTerm(
  terms: readonly string[],
  index: number,
  value: string,
): string[] {
  return terms.map((term, termIndex) => (termIndex === index ? value : term));
}

export function removeTerm(terms: readonly string[], index: number): string[] {
  return terms.filter((_, termIndex) => termIndex !== index);
}

export function mappingValidationError(input: {
  partyId?: string;
  productVariantId?: string;
  terms: readonly string[];
}): MappingValidationError | null {
  if (!input.partyId) return 'party';
  if (!input.productVariantId) return 'product';
  if (normalizeTerms(input.terms).length === 0) return 'terms';
  return null;
}

export function hasProductChange(input: {
  isEdit: boolean;
  savedVariantId?: string;
  selectedVariantId?: string;
}): boolean {
  if (!input.isEdit) return false;
  if (!input.savedVariantId || !input.selectedVariantId) return false;
  return input.selectedVariantId !== input.savedVariantId;
}

export function showSavedProduct(
  isEdit: boolean,
  changingProduct: boolean,
  hasSummary: boolean,
): boolean {
  if (!isEdit) return false;
  if (changingProduct) return false;
  return hasSummary;
}

export function allowDialogClose(nextOpen: boolean, blocked: boolean): boolean {
  if (nextOpen) return true;
  return !blocked;
}

export function decideSave(input: {
  isEdit: boolean;
  partyId?: string;
  productVariantId?: string;
  terms: readonly string[];
  savedVariantId?: string;
}): SaveDecision {
  const error = mappingValidationError(input);
  if (error) return { kind: 'invalid', error };
  const terms = normalizeTerms(input.terms);
  const moving = hasProductChange({
    isEdit: input.isEdit,
    savedVariantId: input.savedVariantId,
    selectedVariantId: input.productVariantId,
  });
  if (moving) return { kind: 'confirm-move' };
  if (input.isEdit) return { kind: 'update', terms };
  return {
    kind: 'create',
    partyId: input.partyId ?? '',
    productVariantId: input.productVariantId ?? '',
    terms,
  };
}

export function updatePayload(input: {
  terms: readonly string[];
  savedVariantId?: string;
  selectedVariantId?: string;
}): { terms: string[]; productVariantId?: string } {
  const terms = normalizeTerms(input.terms);
  const moving = hasProductChange({
    isEdit: true,
    savedVariantId: input.savedVariantId,
    selectedVariantId: input.selectedVariantId,
  });
  if (!moving || !input.selectedVariantId) return { terms };
  return { terms, productVariantId: input.selectedVariantId };
}
